/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("DateTimeHelper", function (context, cls) {
  /**
   * Helpers for time related widgets.
   * @namespace classes.DateTimeHelper
   */
  cls.DateTimeHelper = {
    /** @lends classes.DateTimeHelper */

    /**
     * Converts an Informix DBDATE format (e.g., "MDY4/") into a traditional date format (e.g., "MM/DD/YYYY").
     *
     * Informix DBDATE format defines the order and representation of date parts:
     *   - 'M' = Month
     *   - 'D' = Day
     *   - 'Y' = Year
     * The number following 'Y' specifies the number of digits in the year (e.g., Y4 → YYYY, Y2 → YY)
     *
     * Supported separators: '-', '/', '.', or '0' (where '0' means no separator).
     * Default separator is '/'.
     *
     * @param {string} dbformat - The Informix DBDATE format string (e.g., "MDY4/", "DMY2-", "Y4MD0")
     * @returns {string} The equivalent traditional date format string (e.g., "MM/DD/YYYY")
     */
    parseDbDateFormat: function (dbformat) {
      if (!dbformat || typeof dbformat !== "string") return "";

      // Extract the first valid separator (defaults to '/')
      let sepMatch = dbformat.match(/[./0-]/);
      let sep = sepMatch ? sepMatch[0] : "/";
      if (sep === "0") sep = "";

      let formatParts = [];
      let i = 0;

      while (i < dbformat.length) {
        let char = dbformat[i].toUpperCase();

        if (["M", "D", "Y"].includes(char)) {
          if (char === "Y") {
            // Handle year length (e.g., Y4 → YYYY, Y2 → YY)
            const yearLength = Number.parseInt(dbformat[i + 1], 10);
            const repeat = Number.isNaN(yearLength) ? 2 : yearLength;
            formatParts.push(char.repeat(repeat));
            i += 2; // skip the digit after 'Y'
          } else {
            // Month or Day → always 2 characters (MM or DD)
            formatParts.push(char.repeat(2));
            i++;
          }
        } else {
          // Ignore any unexpected characters (separators are handled globally)
          i++;
        }
      }

      return formatParts.join(sep);
    },

    /**
     * Convert Date time to Locale format
     * @param datetimestring
     * @param format
     * @return {*}
     */
    toDbDateFormat: function (datetimestring, format) {
      // Deprecated by MomentJs but needed for Safari. Using new Date('ISO string') raise invalid date on Safari.
      const localDateTime = context.dayjs(datetimestring);
      // Date object may be valid under Chrome and invalid for other browsers with incomplete date values such as "dd mm" format
      if (localDateTime.isValid()) {
        return localDateTime.format(format);
      }
      return datetimestring;
    },

    /**
     * Convert Date time to ISO format
     * @param datetimestring
     * @param format
     * @returns {*|string}
     */
    toISOFormat: function (datetimestring, format) {
      let currentISODateTime = "";
      if (datetimestring) {
        if (window.browserInfo.isSafari) {
          datetimestring = datetimestring.replaceAll("-", "/");
        }

        const d = context.dayjs(datetimestring, format);
        if (!d.isValid()) {
          return datetimestring;
        }
        const hasSec = format.toLowerCase().includes("s");
        currentISODateTime = d.format("YYYY-MM-DD " + (hasSec ? "HH:mm:ss" : "HH:mm"));
      }
      return currentISODateTime;
    },

    /**
     * Convert Date time to Locale format
     * @param datetimestring
     * @param seconds
     * @returns {string}
     */
    toLocaleFormat: function (datetimestring, seconds) {
      const localDateTime = context.dayjs(datetimestring);
      if (localDateTime.isValid()) {
        return localDateTime.format("L") + " " + localDateTime.format(seconds ? "LTS" : "LT");
      }
      return datetimestring;
    },

    /**
     * Get client locale format
     * @param seconds
     * @returns {string}
     */
    getISOFormat: function (seconds) {
      return "YYYY-MM-DD HH:mm" + (seconds ? ":ss" : "");
    },

    /**
     * Builds a time fragment handling object.
     * Ex: For minutes: group(60) increments from 0 to 59 and wraps.
     * @param {number} highLimit upper limit
     * @returns {{fromText: Function, increaseValue: Function, decreaseValue: Function, getText: Function}}
     */
    timeFragment: function (highLimit) {
      const limit = highLimit;
      const maxChars = String(highLimit).length;
      let value = 0;
      return {
        /**
         * @param {string} text
         * @param {boolean} force
         * @ignore
         * @returns {boolean}
         */
        fromText: function (text, force) {
          if (!text) {
            return false;
          }
          let isComplete = true;
          const intValue = Number.parseInt(text, 10);

          if (Number.isNaN(intValue)) {
            value = 0;
          } else if (text.length > maxChars) {
            value = limit - 1;
          } else if (text.length === maxChars) {
            if (intValue >= limit) {
              value = limit - 1;
            } else {
              value = intValue;
            }
          } else {
            isComplete = false;
          }
          return isComplete;
        },
        /**
         * @ignore
         * @returns {boolean} true if the value has wrapped.
         */
        increaseValue: function () {
          if (value + 1 === limit) {
            value = 0;
            return true;
          }
          value++;
          return false;
        },
        /**
         * @ignore
         * @returns {boolean} true if the value has wrapped
         */
        decreaseValue: function () {
          if (value === 0) {
            value = limit - 1;
            return true;
          }
          value--;
          return false;
        },
        /**
         * @ignore
         * @returns {string} the current value
         */
        getText: function () {
          return value.toString().padStart(2, "0");
        }
      };
    },

    /**
     * Convert traditional year of date to Ming guo year format
     * @param {string} datestring
     * @returns {string} return ming guo year
     */
    mingGuoToGregorianYears: function (datestring) {
      const str = datestring.match(/\d{3}/);
      if (str) {
        const year = str.toString();
        return datestring.replace(year, "" + String(Math.trunc(Number(year) || 0) + 1911));
      }
      return datestring;
    },

    gregorianToMingGuoYears: function (date) {
      return date.getFullYear() - 1911;
    }
  };
});
